import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Layout } from "../components/Layout";
import { BuyCard } from "../components/BuyCard";
import { KpiCard } from "../components/KpiCard";
import { api } from "../lib/api";
import { cn } from "../lib/utils";
import { Clock, AlertTriangle } from "lucide-react";

export default function RooftopBuyList() {
  const { rooftop_id } = useParams();
  const [rooftop, setRooftop] = useState(null);
  const [buys, setBuys] = useState([]);
  const [fit, setFit] = useState([]);
  const [agedItems, setAgedItems] = useState([]);

  const load = useCallback(async () => {
    const [rt, bl, acqs] = await Promise.all([
      api.get(`/rooftops/${rooftop_id}`),
      api.get(`/buy-list?rooftop_id=${rooftop_id}`),
      api.get(`/acquisitions?rooftop_id=${rooftop_id}&limit=80`),
    ]);
    setRooftop(rt.data);
    setBuys(bl.data);
    // Rooftop fit matrix: aggregate by make+model from acquisitions
    const agg = {};
    for (const a of acqs.data) {
      const key = `${a.make} ${a.model}`;
      if (!agg[key]) agg[key] = { units: 0, totalDts: 0, dtsCount: 0 };
      agg[key].units += 1;
      if (a.days_to_sell != null) { agg[key].totalDts += a.days_to_sell; agg[key].dtsCount += 1; }
    }
    const top = Object.entries(agg)
      .map(([k, v]) => ({ ymmt: k, units: v.units, avgDts: v.dtsCount ? Math.round(v.totalDts / v.dtsCount) : null }))
      .sort((a, b) => b.units - a.units)
      .slice(0, 6);
    setFit(top);
    // Aged items = acquisitions > 45 days unsold
    const now = Date.now();
    const aged = acqs.data.filter((a) => !a.retail_date && (now - new Date(a.acquisition_date).getTime()) / 86400000 >= 45)
                  .sort((a, b) => new Date(a.acquisition_date) - new Date(b.acquisition_date))
                  .slice(0, 8);
    setAgedItems(aged);
  }, [rooftop_id]);

  useEffect(() => { load(); }, [load]);

  if (!rooftop) return <Layout variant="rooftop" rooftopId={rooftop_id}><div className="text-sm text-slate-500">Loading…</div></Layout>;

  const strong = buys.filter((b) => b.verdict === "strong_buy").length;
  const buy = buys.filter((b) => b.verdict === "buy").length;
  const cond = buys.filter((b) => b.verdict === "conditional").length;
  const aged45 = agedItems.length;
  const avgGrossProjected = buys.length ? Math.round(buys.reduce((s, b) => s + b.projected_gross, 0) / buys.length) : 0;

  return (
    <Layout variant="rooftop" rooftopId={rooftop_id} rooftopName={rooftop.name}>
      <div className="mb-6 flex items-end justify-between">
        <div>
          <div className="flex items-center gap-3">
            <span className="text-[11px] uppercase tracking-wider font-bold bg-freddy-red text-white px-2 py-1 rounded">{rooftop.brand}</span>
            <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{rooftop.city}, {rooftop.state}</span>
          </div>
          <h1 className="text-3xl font-bold text-navy mt-1">Today's buy list</h1>
          <div className="text-sm text-slate-600 mt-1 font-medium">
            GM <span className="text-navy font-semibold">{rooftop.gm_name}</span> · UCM <span className="text-navy font-semibold">{rooftop.ucm_name}</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard label="Inventory" testId="kpi-rt-inventory" value={buys.length} sub="flagged & open" />
        <KpiCard label="Strong · Buy · Cond" testId="kpi-rt-split" value={`${strong}·${buy}·${cond}`} sub="today's split" />
        <KpiCard label="Aged 45+ days" testId="kpi-rt-aged" value={aged45} sub="action needed" tone={aged45 > 5 ? "alert" : "default"} />
        <KpiCard label="Avg projected gross" testId="kpi-rt-gross" value={`$${avgGrossProjected.toLocaleString()}`} sub="per vehicle on buy list" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 space-y-3" data-testid="buy-list">
          {buys.length === 0 && <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-sm text-slate-500">No buys flagged yet. Check back after the lanes open.</div>}
          {buys.map((v) => <BuyCard key={v.vin} vehicle={v} onAction={load} />)}
        </div>

        <div className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="stock-pressure-widget">
            <div className="flex items-center gap-2 mb-3">
              <Clock size={14} className="text-freddy-red" />
              <h3 className="font-bold text-navy">Stock aging pressure</h3>
            </div>
            {agedItems.length === 0 && <div className="text-sm text-slate-500">No aging pressure — nice turn.</div>}
            <ul className="space-y-2">
              {agedItems.map((a) => {
                const days = Math.round((Date.now() - new Date(a.acquisition_date).getTime()) / 86400000);
                return (
                  <li key={a.acquisition_id} className="flex items-start justify-between gap-2 text-xs">
                    <div className="min-w-0">
                      <div className="font-semibold text-navy truncate">{a.year} {a.make} {a.model} {a.trim}</div>
                      <div className="text-slate-500">Acq ${a.acquisition_price.toLocaleString()} · {days}d on ground</div>
                    </div>
                    <span className={cn("inline-flex shrink-0 px-2 py-0.5 rounded-full text-[10px] font-bold",
                      days >= 75 ? "bg-freddy-red text-white" : days >= 60 ? "bg-verdict-conditional/20 text-verdict-conditionalText" : "bg-slate-100 text-slate-700")}>
                      {days >= 75 ? "wholesale" : days >= 60 ? "reprice" : "watch"}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="rooftop-fit-matrix">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle size={14} className="text-navy" />
              <h3 className="font-bold text-navy">Rooftop fit matrix</h3>
            </div>
            {fit.length === 0 && <div className="text-sm text-slate-500">Not enough history yet.</div>}
            <ul className="space-y-3">
              {fit.map((f) => {
                const fitLevel = f.units >= 8 && f.avgDts && f.avgDts < 40 ? "strong" :
                                 f.units >= 4 && f.avgDts && f.avgDts < 55 ? "buy" :
                                 f.units >= 2 ? "selective" : "avoid";
                const barColor = { strong: "bg-verdict-strong", buy: "bg-verdict-buy", selective: "bg-verdict-conditional", avoid: "bg-freddy-red" }[fitLevel];
                const label = { strong: "Buy aggressively", buy: "Buy at price", selective: "Selective", avoid: "Avoid" }[fitLevel];
                const maxUnits = Math.max(...fit.map(x => x.units), 1);
                return (
                  <li key={f.ymmt}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="font-semibold text-navy truncate">{f.ymmt}</span>
                      <span className="text-slate-500 tabular shrink-0 ml-2">{f.units} · {f.avgDts || "—"}d</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className={cn("h-full rounded-full", barColor)} style={{ width: `${(f.units / maxUnits) * 100}%` }} />
                    </div>
                    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mt-0.5">{label}</div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </Layout>
  );
}
